/**
 * 投放计划页面 — DeliveryPlans（二期批2 翻新，对齐概念稿 delivery-plans.html）。
 *
 * GET  /api/delivery-plans?status&product_id&company_id   列表（选择器过滤）
 * POST /api/delivery-plans/{id}/confirm                   确认（pending_confirm → confirmed）
 * POST /api/delivery-plans/{id}/complete                  登记完成（confirmed → delivered）
 *
 * 结构：4 指标卡 → 工具栏（状态桶）→ 计划表（状态机流转列 + 费用/日期/GHS 前后）→
 *       g2 说明卡（费用口径 + 闭环状态）。
 * 状态机：pending_confirm → confirmed → delivered（cancelled 旁路）。
 * 写操作只有两个轻量入口：确认 / 登记完成（数据从 Agent 录入，不做新建）。
 *
 * 概念稿裁剪：计划篇数/已发篇数/进度条无接口字段（计划按媒体×费用建模，
 * 不按篇数），跳过不虚构；费用/日期/GHS 前后均为真实字段。
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import PageHeader from "../components/PageHeader";
import StatCard from "../components/StatCard";
import DataTable, { type DataTableColumn } from "../components/DataTable";
import Badge from "../components/Badge";
import ConfirmDialog from "../components/ConfirmDialog";
import Loading from "../components/Loading";
import EmptyState from "../components/EmptyState";
import { completePlan, confirmPlan, getDeliveryPlans } from "../api/delivery";
import { useCompanyStore } from "../store/companyStore";
import { DELIVERY_STATUS_MAP } from "../utils/constants";
import { formatCurrency, formatDate, formatDateTime } from "../utils/format";
import type { DeliveryPlan } from "../types";

/** 状态机三步（主流水线；cancelled 旁路不展示步骤）。 */
const FLOW_STEPS = [
  { key: "pending_confirm", label: "待确认" },
  { key: "confirmed", label: "已确认" },
  { key: "delivered", label: "已投放" },
] as const;

/** 状态机步骤点（当前步高亮，已过步成功色）。 */
function FlowSteps({ status }: { status: string }) {
  if (status === "cancelled") {
    return <Typography sx={{ fontSize: 11, color: "error.main" }}>已取消</Typography>;
  }
  const activeIdx = FLOW_STEPS.findIndex((s) => s.key === status);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {FLOW_STEPS.map((step, i) => {
        const reached = i <= activeIdx;
        const isCurrent = i === activeIdx;
        return (
          <Box key={step.key} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {i > 0 && (
              <Box
                sx={{
                  width: 12,
                  height: 1,
                  bgcolor: reached ? "success.main" : "divider",
                }}
              />
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: reached
                    ? isCurrent
                      ? "primary.main"
                      : "success.main"
                    : "divider",
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: 11,
                  color: isCurrent
                    ? "primary.main"
                    : reached
                      ? "success.main"
                      : "text.disabled",
                  fontWeight: isCurrent ? 600 : 400,
                  whiteSpace: "nowrap",
                }}
              >
                {step.label}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

/** 状态桶徽标。 */
function BucketChip({
  label,
  count,
  active,
  color,
  bg,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  color: string;
  bg: string;
  onClick: () => void;
}) {
  return (
    <Chip
      label={`${label} ${count}`}
      size="small"
      onClick={onClick}
      sx={{
        fontSize: 11,
        height: 22,
        fontWeight: 500,
        cursor: "pointer",
        color: active ? color : "text.secondary",
        bgcolor: active ? bg : "semanticBg.neutral",
        border: "1px solid",
        borderColor: active ? color : "transparent",
      }}
    />
  );
}

export default function DeliveryPlans() {
  const [plans, setPlans] = useState<DeliveryPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // 从全局 companyStore 读取选择器
  const selectedCompanyId = useCompanyStore((s) => s.selectedCompanyId);
  const selectedProductId = useCompanyStore((s) => s.selectedProductId);

  // 确认弹窗
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmingPlan, setConfirmingPlan] = useState<DeliveryPlan | null>(null);
  const [confirming, setConfirming] = useState(false);

  // 登记完成弹窗
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completingPlan, setCompletingPlan] = useState<DeliveryPlan | null>(null);
  const [completing, setCompleting] = useState(false);
  const [actualCost, setActualCost] = useState("");
  const [deliveryUrl, setDeliveryUrl] = useState("");
  const [actualDate, setActualDate] = useState("");
  const [completeError, setCompleteError] = useState("");

  // 成功提示
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");

  /** 加载投放计划列表（选择器过滤生效）。 */
  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await getDeliveryPlans({
        status: statusFilter || undefined,
        product_id: selectedProductId || undefined,
        company_id: selectedCompanyId || undefined,
      });
      setPlans(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "获取投放计划失败");
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, selectedCompanyId, selectedProductId]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  /** 状态计数与费用聚合（当前选择器范围内）。 */
  const stats = useMemo(() => {
    const count = (s: string) => plans.filter((p) => p.status === s).length;
    const sumEst = (s: string) =>
      plans.filter((p) => p.status === s).reduce((a, p) => a + (p.estimated_cost || 0), 0);
    const actualSum = plans
      .filter((p) => p.status === "delivered")
      .reduce((a, p) => a + (p.actual_cost || 0), 0);
    const totalEst = plans.reduce((a, p) => a + (p.estimated_cost || 0), 0);
    return {
      pending: count("pending_confirm"),
      confirmed: count("confirmed"),
      delivered: count("delivered"),
      cancelled: count("cancelled"),
      pendingEst: sumEst("pending_confirm"),
      deliveredActual: actualSum,
      totalEst,
    };
  }, [plans]);

  /** 打开确认弹窗。 */
  const handleOpenConfirm = (plan: DeliveryPlan) => {
    setConfirmingPlan(plan);
    setConfirmOpen(true);
  };

  /** 执行确认。 */
  const handleConfirm = async () => {
    if (!confirmingPlan) return;
    setConfirming(true);
    try {
      const updated = await confirmPlan(confirmingPlan.id);
      setPlans((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
      setSnackMsg(`已确认投放计划：${updated.media_name}`);
      setSnackOpen(true);
      setConfirmOpen(false);
      setConfirmingPlan(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "确认失败");
      setConfirmOpen(false);
    } finally {
      setConfirming(false);
    }
  };

  /** 打开登记完成弹窗。 */
  const handleOpenComplete = (plan: DeliveryPlan) => {
    setCompletingPlan(plan);
    setActualCost(plan.actual_cost != null ? String(plan.actual_cost) : String(plan.estimated_cost));
    setDeliveryUrl(plan.delivery_url || "");
    setActualDate(plan.actual_date || new Date().toISOString().slice(0, 10));
    setCompleteError("");
    setCompleteOpen(true);
  };

  /** 执行登记完成。 */
  const handleComplete = async () => {
    if (!completingPlan) return;
    setCompleting(true);
    setCompleteError("");
    try {
      const updated = await completePlan(completingPlan.id, {
        actual_cost: actualCost ? Number(actualCost) : undefined,
        delivery_url: deliveryUrl || undefined,
        actual_date: actualDate || undefined,
      });
      setPlans((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
      setSnackMsg(`已登记投放完成：${updated.media_name}`);
      setSnackOpen(true);
      setCompleteOpen(false);
      setCompletingPlan(null);
    } catch (err) {
      setCompleteError(err instanceof Error ? err.message : "登记失败，请稍后重试");
    } finally {
      setCompleting(false);
    }
  };

  /** 表格列定义。 */
  const columns: DataTableColumn<DeliveryPlan>[] = [
    {
      key: "media",
      label: "关联文案（文案库）",
      render: (row) => (
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
            {row.copy_title || row.media_name}
          </Typography>
          {row.notes && (
            <Typography
              sx={{
                fontSize: 11,
                color: "text.secondary",
                maxWidth: 280,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.notes}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: "media_names",
      label: "投放媒体（投放库）",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {(row.delivery_media_names && row.delivery_media_names.length > 0
            ? row.delivery_media_names
            : [row.media_name]
          ).join(" · ")}
        </Typography>
      ),
    },
    {
      key: "product",
      label: "关联产品",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {row.product_name_snapshot ||
            (row.product_name && row.product_name !== "未知产品"
              ? row.product_name
              : "（已删除产品）")}
        </Typography>
      ),
    },
    {
      key: "cost",
      label: "费用（计划 / 实际）",
      align: "right",
      render: (row) => (
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
            {formatCurrency(row.estimated_cost)}
          </Typography>
          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
            实际 {row.actual_cost != null ? formatCurrency(row.actual_cost) : "—"}
          </Typography>
        </Box>
      ),
    },
    {
      key: "date",
      label: "日期（计划 / 实际）",
      render: (row) => (
        <Box>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            {formatDate(row.planned_date)}
          </Typography>
          <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
            实际 {row.actual_date ? formatDate(row.actual_date) : "—"}
          </Typography>
        </Box>
      ),
    },
    {
      key: "flow",
      label: "状态机流转",
      render: (row) => <FlowSteps status={row.status} />,
    },
    {
      key: "status",
      label: "状态",
      align: "center",
      render: (row) => {
        const info = DELIVERY_STATUS_MAP[row.status] || {
          label: row.status,
          color: "text.secondary",
          bg: "semanticBg.neutral",
        };
        return <Badge label={info.label} color={info.color} bgColor={info.bg} />;
      },
    },
    {
      key: "ghs",
      label: "GHS 前后",
      align: "center",
      render: (row) =>
        row.pre_delivery_ghs != null ? (
          <Typography
            sx={{
              fontSize: 12,
              color:
                row.post_delivery_ghs != null
                  ? row.post_delivery_ghs >= row.pre_delivery_ghs
                    ? "success.main"
                    : "error.main"
                  : "text.secondary",
            }}
          >
            {row.pre_delivery_ghs} →{" "}
            {row.post_delivery_ghs != null ? row.post_delivery_ghs : "?"}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 12, color: "text.disabled" }}>—</Typography>
        ),
    },
    {
      key: "confirmed",
      label: "确认信息",
      render: (row) =>
        row.confirmed_at ? (
          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
            {row.confirmed_by || "—"}
            <br />
            {formatDateTime(row.confirmed_at)}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 11, color: "text.disabled" }}>—</Typography>
        ),
    },
    {
      key: "action",
      label: "操作",
      align: "center",
      render: (row) =>
        row.status === "pending_confirm" ? (
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenConfirm(row);
            }}
          >
            确认
          </Button>
        ) : row.status === "confirmed" ? (
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenComplete(row);
            }}
          >
            登记完成
          </Button>
        ) : row.delivery_url ? (
          <Button
            size="small"
            variant="text"
            href={row.delivery_url}
            target="_blank"
            rel="noreferrer"
            sx={{ fontSize: 12 }}
          >
            查看链接
          </Button>
        ) : (
          <Typography sx={{ fontSize: 11, color: "text.disabled" }}>—</Typography>
        ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="投放计划"
        breadcrumbs={[{ label: "媒体投放" }, { label: "投放计划" }]}
        subtitle="文案库选一篇文案 × 媒体投放库选一组媒体 · 确认(锁定) → 发布(下发)"
        actions={
          stats.pending > 0 ? (
            <Badge
              label={`${stats.pending} 个计划待确认`}
              variant="warning"
              size="medium"
            />
          ) : undefined
        }
      />

      {/* 4 指标卡 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="计划总数"
            value={loading ? "—" : plans.length}
            subtitle={`预估费用合计 ${formatCurrency(stats.totalEst)}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="待确认"
            value={loading ? "—" : stats.pending}
            subtitle={
              stats.pending > 0
                ? `待确认预估 ${formatCurrency(stats.pendingEst)}`
                : "无待办确认"
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="进行中（已确认）"
            value={loading ? "—" : stats.confirmed}
            subtitle="已确认待投放执行"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="已投放"
            value={loading ? "—" : stats.delivered}
            subtitle={`实际费用合计 ${formatCurrency(stats.deliveredActual)}`}
          />
        </Grid>
      </Grid>

      {/* 工具栏：状态桶 */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <BucketChip
              label="全部"
              count={plans.length}
              active={statusFilter === ""}
              color="primary.main"
              bg="brand.50"
              onClick={() => setStatusFilter("")}
            />
            <BucketChip
              label="待确认"
              count={stats.pending}
              active={statusFilter === "pending_confirm"}
              color="warning.main"
              bg="semanticBg.warning"
              onClick={() => setStatusFilter("pending_confirm")}
            />
            <BucketChip
              label="已确认"
              count={stats.confirmed}
              active={statusFilter === "confirmed"}
              color="info.main"
              bg="semanticBg.info"
              onClick={() => setStatusFilter("confirmed")}
            />
            <BucketChip
              label="已投放"
              count={stats.delivered}
              active={statusFilter === "delivered"}
              color="success.main"
              bg="semanticBg.success"
              onClick={() => setStatusFilter("delivered")}
            />
            <BucketChip
              label="已取消"
              count={stats.cancelled}
              active={statusFilter === "cancelled"}
              color="error.main"
              bg="semanticBg.error"
              onClick={() => setStatusFilter("cancelled")}
            />
          </Box>
        </CardContent>
      </Card>

      {/* 错误提示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* 数据表格 */}
      {loading ? (
        <Loading fullPage text="正在加载投放计划..." />
      ) : (
        <DataTable
          columns={columns}
          data={plans}
          showPagination={false}
          getRowId={(row) => row.id}
          emptyText="暂无投放计划"
        />
      )}

      {/* g2：费用口径 + 闭环状态 */}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <Typography variant="h4">费用口径说明</Typography>
                <Badge label="计划 / 实际" variant="info" />
              </Box>
              <Typography sx={{ fontSize: 13, lineHeight: 1.8 }}>
                「费用」列上行为计划预估费用（Agent 建计划时登记），下行
                「实际」为登记完成后回填的真实支出；费用统计页按产品归集
                预估口径，已投放计划的实际费用以本页登记为准。
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <Typography variant="h4">投放闭环状态</Typography>
                <Badge label="二期续建" />
              </Box>
              <Typography sx={{ fontSize: 13, lineHeight: 1.8 }}>
                发文回传自动入台账、T+7 复诊报告、漏斗归因回填属二期续建；
                当前可通过「登记完成」回填实际费用与投放链接，再经收录台账
                手工录入 URL 追踪收录，形成半自动闭环。
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 确认弹窗 */}
      <ConfirmDialog
        open={confirmOpen}
        title="确认（锁定）投放计划"
        content={
          confirmingPlan
            ? `确认并锁定投放计划「${confirmingPlan.media_name}」（文案：${confirmingPlan.copy_title || "—"}）？预估费用 ${formatCurrency(confirmingPlan.estimated_cost)}，计划日期 ${formatDate(confirmingPlan.planned_date)}。确认后状态变更为"已确认"，进入排期；随后可单独点"发布"下发到媒体资源平台。`
            : ""
        }
        confirmText="确认锁定"
        onConfirm={handleConfirm}
        onCancel={() => {
          setConfirmOpen(false);
          setConfirmingPlan(null);
        }}
        loading={confirming}
      />

      {/* 登记完成弹窗 */}
      <Dialog
        open={completeOpen}
        onClose={() => !completing && setCompleteOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>登记投放完成</DialogTitle>
        <DialogContent>
          {completingPlan && (
            <>
              <Typography sx={{ fontSize: 13, mb: 2 }}>
                {completingPlan.media_name} · 计划日期{" "}
                {formatDate(completingPlan.planned_date)} · 预估{" "}
                {formatCurrency(completingPlan.estimated_cost)}
              </Typography>
              <TextField
                fullWidth
                size="small"
                label="实际费用（元）"
                type="number"
                value={actualCost}
                onChange={(e) => setActualCost(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                size="small"
                label="投放链接（可选）"
                value={deliveryUrl}
                onChange={(e) => setDeliveryUrl(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                size="small"
                label="实际投放日期"
                type="date"
                value={actualDate}
                onChange={(e) => setActualDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 1 }}>
                登记后状态变更为「已投放」，实际费用进入费用统计归集。
              </Typography>
              {completeError && (
                <Alert severity="error" sx={{ mt: 1.5 }}>
                  {completeError}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setCompleteOpen(false)}
            disabled={completing}
            color="inherit"
          >
            取消
          </Button>
          <Button variant="contained" onClick={handleComplete} disabled={completing}>
            {completing ? "提交中..." : "登记完成"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 成功提示 */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSnackOpen(false)}>
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
